/* ==========================================================================
   Điểm vào của bản nhúng bằng thẻ <script>.

   Trong SPA chính thì dùng gói npm và gọi `init()` — chỉ ở đó mới truyền được
   `getToken` và `onAction`, hai thứ không diễn đạt được bằng chuỗi data-*.
   File này dành cho các bề mặt không có bundler.

   Khác `src/index.ts`: file này TỰ CHẠY và mở một hàng lệnh toàn cục.
   ========================================================================== */

import type { OpenContext, ApusAssistantApi, WidgetOptions } from "./types.ts";
import { fromScript } from "./core/config.ts";
import { Assistant, VERSION } from "./widget.ts";
import { whenReady } from "./index.ts";

type QueuedCall = IArguments | unknown[];
interface CommandQueue {
  (...args: unknown[]): unknown;
  q?: QueuedCall[];
}

/* Phải đọc currentScript NGAY, đồng bộ: sau lượt tick đầu tiên trình duyệt đã
   trả nó về null. */
const SCRIPT = (document.currentScript as HTMLScriptElement | null) || findScript();

function findScript(): HTMLScriptElement | null {
  const nodes = document.querySelectorAll<HTMLScriptElement>("script[data-apus-assistant]");
  return nodes.length ? (nodes[nodes.length - 1] as HTMLScriptElement) : null;
}

const scriptOptions: WidgetOptions = fromScript(SCRIPT);
/* linh vật Apus Ami nằm cạnh bản build: <gốc>/dist/apus-assistant.js → <gốc>/assets/ami/ami-head.webp */
if (!scriptOptions.mascotUrl && SCRIPT?.src && /^https?:/i.test(SCRIPT.src)) {
  try {
    scriptOptions.mascotUrl = new URL("../assets/ami/ami-head.webp", SCRIPT.src).href;
  } catch {
    /* URL lạ: dùng biểu tượng mặc định */
  }
}
let instance: ApusAssistantApi | null = null;

function ensure(options: WidgetOptions = {}): ApusAssistantApi {
  if (instance) {
    instance.update(options);
    return instance;
  }
  const merged: WidgetOptions = { ...scriptOptions, ...options };
  const assistant = new Assistant(merged);
  assistant.markExplicit(Object.keys(merged));
  /* Nhả singleton khi instance bị gỡ, bất kể gỡ bằng đường nào. Không có dòng
     này thì React StrictMode (mount → unmount → mount trong dev) sẽ nhận lại
     đúng cái instance vừa destroy, và panel không bao giờ hiện lại. */
  assistant.on("destroy", () => {
    if (instance === assistant) instance = null;
  });
  instance = assistant;
  whenReady(() => assistant.mount());
  return assistant;
}

/** Tên lệnh trùng với phương thức trên API, cộng "init" và "destroy". */
function run(command: string, ...args: unknown[]): unknown {
  if (command === "init") return ensure((args[0] as WidgetOptions) || {});

  if (command === "destroy") {
    instance?.destroy();
    instance = null;
    return undefined;
  }

  const api = ensure() as unknown as Record<string, unknown>;
  const method = api[command];
  if (typeof method !== "function") {
    console.warn('[ApusAssistant] không có lệnh "' + command + '".');
    return undefined;
  }
  return (method as (...a: unknown[]) => unknown).apply(api, args);
}

/* Nhận lại hàng đợi mà snippet đã dựng trước khi file này tải xong, để trang
   gọi được apus("open") ngay cả với script async. */
const existing = (window as unknown as { apus?: CommandQueue }).apus;
const queued: QueuedCall[] = Array.isArray(existing?.q) ? (existing.q as QueuedCall[]) : [];

const apus: CommandQueue = function (...args: unknown[]) {
  return run(String(args[0]), ...args.slice(1));
};

(window as unknown as { apus: CommandQueue }).apus = apus;

const api = {
  /* Phải là `ensure`, không phải `init` của index.ts. Hai hàm đó trông giống
     nhau nhưng `init` tạo instance rời khỏi singleton — app gọi init() rồi
     open() sẽ có hai panel, hai shadow root, hai phiên chat. */
  init: (options?: WidgetOptions): ApusAssistantApi => ensure(options ?? {}),
  get instance(): ApusAssistantApi | null {
    return instance;
  },
  get(): ApusAssistantApi {
    return ensure();
  },
  /** Đường tắt hay dùng nhất từ nút Ask của app chủ. */
  open(context?: OpenContext): void {
    ensure().open(context);
  },
  Assistant,
  VERSION,
};

(window as unknown as { ApusAssistant: typeof api }).ApusAssistant = api;

/* data-apus-assistant trên thẻ script = ý định dựng ngay. */
if (SCRIPT?.hasAttribute("data-apus-assistant") && SCRIPT.dataset.auto !== "false") {
  ensure();
}

for (const call of queued) {
  const args = Array.prototype.slice.call(call) as unknown[];
  try {
    run(String(args[0]), ...args.slice(1));
  } catch (error) {
    console.warn("[ApusAssistant] lệnh trong hàng đợi lỗi", error);
  }
}

/* Không `export` gì cả: bundle IIFE chạy xong là xong, mọi thứ công khai đã
   nằm ở window.ApusAssistant và window.apus. */
export {};
